'use client'

import { Link2, AtSign, Play, Mail, MapPin, Phone } from 'lucide-react'
import { useT } from '@/lib/useT'

const footerLinkKeys = {
  'footer.product': [
    { key: 'footer.features', href: '/#features' },
    { key: 'footer.pricing', href: '/#pricing' },
    { key: 'footer.integrations', href: '#' },
  ],
  'footer.company': [
    { key: 'footer.about', href: '#' },
    { key: 'footer.careers', href: '/careers' },
    { key: 'footer.blog', href: '#' },
  ],
  'footer.support': [
    { key: 'footer.docs', href: '#' },
    { key: 'footer.contact', href: '#' },
  ],
  'footer.legal': [
    { key: 'footer.privacy', href: '/privacy' },
    { key: 'footer.terms', href: '/terms' },
  ],
}

const social = [
  { icon: Link2, label: 'LinkedIn', href: '#' },
  { icon: AtSign, label: 'X (Twitter)', href: '#' },
  { icon: Play, label: 'YouTube', href: '#' },
]

export default function Footer() {
  const t = useT()

  return (
    <footer className="relative overflow-hidden bg-nature-deep" role="contentinfo">
      <div className="sidr-pattern absolute -end-40 -top-24 h-[480px] w-[560px] opacity-20" aria-hidden="true" />

      <div className="relative mx-auto max-w-[1200px] px-6 pt-16 pb-10">
        {/* Top grid */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-10 mb-14">
          {/* Brand column - spans 2 cols */}
          <div className="col-span-2 flex flex-col gap-5">
            <a href="/" aria-label="SIDR home" className="self-start">
              <img src="/brand/sidr-logo-on-dark.svg" alt="SIDR" className="h-20 w-auto" />
            </a>
            <p className="text-sm text-white/70 leading-relaxed max-w-[240px]">
              {t('footer.tagline')}
            </p>

            {/* Contact mini-block */}
            <address className="not-italic flex flex-col gap-2.5 text-xs text-white/65">
              <span className="flex items-center gap-2">
                <MapPin size={12} aria-hidden="true" className="shrink-0 text-[#00FF8B]/60" />
                Olaya Business Center, Al Olaya, Al Olaya District, 12244 - Riyadh, KSA.
                Office No.26, 2nd Floor, Building 7505.
              </span>
              <a href="mailto:info@alnajahksa.com" className="flex items-center gap-2 hover:text-[#00FF8B] transition-colors duration-140">
                <Mail size={12} aria-hidden="true" className="shrink-0 text-[#00FF8B]/60" />
                info@alnajahksa.com
              </a>
              <a href="tel:+966535353311" className="flex items-center gap-2 hover:text-[#00FF8B] transition-colors duration-140">
                <Phone size={12} aria-hidden="true" className="shrink-0 text-[#00FF8B]/60" />
                +966 5353 53311
              </a>
            </address>

            {/* Social */}
            <div className="flex items-center gap-2">
              {social.map(({ icon: Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  className="flex h-8 w-8 items-center justify-center rounded-[8px] text-white/60 hover:text-[#00FF8B] hover:bg-[#F1F5F6]/6 transition-all duration-140 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00B785]"
                >
                  <Icon size={16} aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(footerLinkKeys).map(([groupKey, links]) => (
            <div key={groupKey} className="flex flex-col gap-4">
              <h3 className="text-[10px] font-bold uppercase tracking-[0.14em] text-leaf">
                {t(groupKey as any)}
              </h3>
              <ul className="flex flex-col gap-2" role="list">
                {links.map((link) => (
                  <li key={link.key}>
                    <a
                      href={link.href}
                      className="text-sm text-white/75 hover:text-[#00FF8B] transition-colors duration-[140ms] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#00B785] rounded-sm"
                    >
                      {t(link.key as any)}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* CTA band */}
        <div className="rounded-lg bg-digital px-8 py-8 mb-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col gap-1 text-center md:text-start">
            <p className="text-lg font-medium text-nature">{t('footer.ctaHeading')}</p>
            <p className="text-sm text-nature/80">{t('footer.ctaDescription')}</p>
          </div>
          <a
            href="/#signup"
            className="btn-press shrink-0 rounded-md bg-nature px-6 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-white hover:bg-nature-hover transition-colors whitespace-nowrap"
          >
            {t('nav.getStarted')}
          </a>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-white/10 pt-8">
          <p className="text-xs text-white/60">
            {t('footer.copyright')}
          </p>
          <div className="flex items-center gap-4">
            <a href="/privacy" className="text-xs text-white/60 hover:text-white transition-colors duration-140">{t('footer.privacy')}</a>
            <a href="/terms" className="text-xs text-white/60 hover:text-white transition-colors duration-140">{t('footer.terms')}</a>
          </div>
        </div>
      </div>
    </footer>
  )
}
